import { Box, Button } from '@mui/material'
import Header from 'components/Header'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useBasePath } from 'components/Providers'
import IncidentsList from './List'
import Filters from 'components/Filters'
import DateRangePicker from 'components/DateRangePicker/DateRangePicker'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getGradeLevelAndSections } from 'store/lookup'
import { INCIDENT_TYPE_OPTIONS } from 'constants'
import moment from 'moment'
import { hasFullPermission } from 'store'

const DEFAULT_LIMIT = 10

const getDefaultQuery = () => ({
  startDate: moment().format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  limit: DEFAULT_LIMIT,
  offset: 0,
})

export default function IncidentListIndex() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const dispatch = useDispatch()
  const [query, setQuery] = useState(getDefaultQuery())

  const gradeLevelAndSections = useSelector(state => state.lookup.gradeLevelAndSections)
  const sessionAcademicYear = useSelector(state => state.session.academicYear)

  useEffect(() => {
    if (!gradeLevelAndSections) dispatch(getGradeLevelAndSections())
  }, [dispatch, Boolean(gradeLevelAndSections)])

  const gradeOptions = useMemo(
    () =>
      gradeLevelAndSections?.flatMap(el => [
        { value: `L:${el.id}`, label: `${t('Grade')} ${el.id}`, gradeLevelId: el.id, gradeSectionId: null },
        ...el.section.map(s => ({
          value: `S:${s.id}`,
          label: `${t('Section')} ${s.gradeSection}`,
          gradeLevelId: el.id,
          gradeSectionId: s.id,
        })),
      ]) ?? [],
    [gradeLevelAndSections, t],
  )

  let gradeValue = ''
  if (query.gradeSectionId) gradeValue = `S:${query.gradeSectionId}`
  else if (query.gradeLevelId) gradeValue = `L:${query.gradeLevelId}`

  const handleChange = newQuery => {
    const next = { ...newQuery }
    const grade = next.grade
    delete next.grade
    delete next.gradeLevelId
    delete next.gradeSectionId
    if (grade?.startsWith('S:')) next.gradeSectionId = Number(grade.slice(2))
    else if (grade?.startsWith('L:')) next.gradeLevelId = grade.slice(2)
    setQuery({ ...next, offset: 0 })
  }

  const handleDateRange = useCallback(range => {
    setQuery(prev => ({
      ...prev,
      startDate: range[0].format('YYYY-MM-DD'),
      endDate: range[1].format('YYYY-MM-DD'),
      offset: 0,
    }))
  }, [])

  const filtersConfig = useMemo(
    () => [
      {
        key: 'grade',
        label: t('Grade Level / Section'),
        type: 'autocomplete',
        options: gradeOptions,
        gridProps: { xs: 12, sm: 6, md: 2 },
        renderOption: ({ style, ...props }, option) => (
          <li {...props} key={option.value} style={{ ...style, paddingLeft: option.gradeSectionId ? 40 : 16 }}>
            {option.label}
          </li>
        ),
      },
      {
        key: 'studentId',
        label: t('Student'),
        type: 'student',
        valueField: 'personId',
        dependsOn: 'grade',
        scopeBy: { gradeLevelId: 'gradeLevelId', gradeSectionId: 'gradeSectionId' },
        gridProps: { xs: 12, sm: 6, md: 3 },
      },
      {
        key: 'staffId',
        label: t('Staff'),
        type: 'staff',
        valueField: 'personId',
        gridProps: { xs: 12, sm: 6, md: 3 },
      },
      {
        key: 'incidentType',
        label: t('Type'),
        type: 'autocomplete',
        options: INCIDENT_TYPE_OPTIONS.map(option => ({ ...option, label: t(option.label) })),
        gridProps: { xs: 12, sm: 6, md: 2 },
      },
      {
        key: 'dateRange',
        type: 'custom',
        gridProps: { xs: 12, sm: 6, md: 2 },
        render: ({ query: current }) => (
          <DateRangePicker
            label={t('Incident Date Range')}
            startDate={current?.startDate}
            endDate={current?.endDate}
            setDateRange={handleDateRange}
            minDate={sessionAcademicYear.startDate}
            maxDate={sessionAcademicYear.endDate}
          />
        ),
      },
    ],
    [t, gradeOptions, handleDateRange, sessionAcademicYear],
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Header title={t('Incidents')} small />
        {hasFullPermission('discipline.incident') && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => navigate(`${basePath}/new`)}
            sx={{ mr: 2 }}
          >
            {t('Add Incident')}
          </Button>
        )}
      </Box>

      <Filters
        filtersConfig={filtersConfig}
        values={{ ...query, grade: gradeValue }}
        onChange={handleChange}
        gridContainerProps={{ sx: { p: 1, width: '100%', flexWrap: 'wrap' } }}
      />

      <IncidentsList query={query} onQueryChange={setQuery} />
    </Box>
  )
}
